import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getCheckoutStatus } from "../lib/api";
import { useCart } from "../context/CartContext";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import NoIndex from "../components/NoIndex";

const MAX_ATTEMPTS = 8;
const INTERVAL_MS = 2000;

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired | timeout
  const [data, setData] = useState(null);
  const attemptsRef = useRef(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await getCheckoutStatus(sessionId);
        setData(res);
        if (res.payment_status === "paid") {
          setStatus("paid");
          if (!clearedRef.current) {
            clear();
            clearedRef.current = true;
          }
          return;
        }
        if (res.status === "expired") {
          setStatus("expired");
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      } catch (e) {
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("failed");
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      }
    };
    poll();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16" data-testid="success-page">
      <NoIndex title="Order Confirmed" />
      <div className="pw-card p-8 sm:p-12 text-center">
        {status === "checking" && (
          <>
            <Loader2 size={56} strokeWidth={3} className="mx-auto mb-4 animate-spin" />
            <h1 className="font-display text-3xl uppercase mb-3">Confirming your order...</h1>
            <p className="opacity-70">Hold tight, we&apos;re checking your payment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={64} strokeWidth={3} className="mx-auto mb-4 text-[#39FF14] drop-shadow-[2px_2px_0px_rgba(10,10,10,1)]" />
            <h1 className="font-display text-4xl sm:text-5xl uppercase mb-3" data-testid="success-title">
              Woohoo! Order Confirmed
            </h1>
            <p className="text-lg mb-6">
              Thanks for joining the Playworld crew! A confirmation email is on its way.
            </p>
            {data && (
              <div className="bg-[#FFFDF0] border-3 border-black rounded-xl p-4 mb-6 text-left" style={{ borderWidth: 3 }}>
                <p className="font-bold mb-2">Order Summary</p>
                {(data.items || []).map((it, idx) => (
                  <div key={idx} className="flex justify-between text-sm py-1">
                    <span>
                      {it.name} {it.size ? `(${it.size})` : ""} × {it.quantity}
                    </span>
                    <span className="font-bold">${(it.line_total ?? it.price * it.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between font-display text-xl mt-3 pt-3 border-t-2 border-black">
                  <span>Total</span>
                  <span data-testid="success-total">${(data.amount_total / 100).toFixed(2)}</span>
                </div>
              </div>
            )}
            <Link to="/shop" className="pw-btn" data-testid="success-continue-shopping">
              Continue Shopping
            </Link>
          </>
        )}
        {(status === "failed" || status === "expired" || status === "timeout") && (
          <>
            <AlertTriangle size={56} strokeWidth={3} className="mx-auto mb-4 text-[#FF2E93]" />
            <h1 className="font-display text-3xl uppercase mb-3" data-testid="success-error-title">
              {status === "expired"
                ? "Payment session expired"
                : status === "timeout"
                  ? "Still processing..."
                  : "Something went wrong"}
            </h1>
            <p className="mb-6 opacity-80">
              {status === "timeout"
                ? "We're still confirming your payment. Please check your email for the receipt."
                : "If you were charged, please contact support — we'll sort it out."}
            </p>
            <Link to="/cart" className="pw-btn pw-btn-blue">Back to Cart</Link>
          </>
        )}
      </div>
    </div>
  );
}
